import React, { KeyboardEvent, ReactElement, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Date } from '../../../../../common/src/calendar.ts';
import {
  addDayOffset,
  dateFromString,
  DateRange,
  dateRangeOverlapsDate,
  DateString,
  dateStringFromDate,
  daysBetween,
  getDateAsShortString,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getMonthName,
  maxDate,
  minDate,
  todayDate,
} from '../../../../../common/src/date_string.ts';
import {
  ClientOrg,
  EventSongList,
  HymnOfTheMonthSongList,
  IntervalSongList,
  IntervalType,
  Org,
  OrgType,
  SongList,
  SongListEntry,
  SongListEntryType,
  SongListType,
} from '../../../../../common/src/model.ts';
import {
  getSongListEndDate,
  getSongListStartDate,
  getWeeklySongListType,
  isBound,
} from '../../../../../common/src/model_util.ts';
import {
  getSongEntriesForDate,
  getWeeklyDateForOrganization,
  isWeeklySongList,
  isWeeklySongListForDate,
} from '../../../../../common/src/org.ts';
import { Pages } from '../../../../../common/src/pages.ts';
import { ensureUnreachable, NonEmptyArray } from '../../../../../common/src/util.ts';
import DashboardIcon from '../../../assets/dashboard.svg?react';
import { localStorageGet, LocalStorageKey } from '../../../data/client_local_storage.ts';
import {
  refreshSingingPlans,
  songFutureSizeInWeeks,
  synchronizeAllSongLists,
  synchronizeOrgsWithServer,
  useCurrentOrg,
} from '../../../data/use_current_org.tsx';
import { useCustomMusic } from '../../../data/use_custom_music.tsx';
import { HymnalsContext } from '../../../data/use_hymnals.tsx';
import { useLocalStorage } from '../../../data/use_local_storage.tsx';
import { useUserAttributes } from '../../../data/use_user_attributes.ts';
import { confirm } from '../../../util/confirm.tsx';
import { goBack } from '../../../util/go_back.ts';
import { getDisplayNameForSongListEntry, getUrlForPage, resolveSearchStringToSongSlug } from '../../../util/path.ts';
import { getPlatform, Platform } from '../../../util/platform.ts';
import { getTermForDateRange, getTermWeekNumber } from '../../../util/school.ts';
import { OuterPageContent } from '../../../util/shared.tsx';
import { Spinner } from '../../../util/spinner.tsx';
import { SyncAnimation } from '../../../util/sync_animation.tsx';
import {
  HeaderButtonBorderRadius,
  HeaderButtonPaddingDesktop,
  UploadSongButton,
} from '../../../util/upload_song_button.tsx';
import { useCurrentDate } from '../../../util/use_current_date.tsx';

import { useEffectOnce } from '../../../util/use_effect_once.ts';
import { useHoldAppReload } from '../../../util/use_hold_app_reload.ts';
import { DownloadSongSheetsButton } from './download_song_sheets.tsx';
import { PlannerSchemaColumn } from './planner_schema_column.tsx';
import {
  BackButton,
  BackButtonPlaceholder,
  BackButtonWidth,
  DateRowTop,
  MinSongListRowCount,
  PlannerButtonIconThreshold,
  PlannerHeaderButtonGap,
  PlannerYearHeight,
  SongRowHeightDouble,
  SongRowHeightSingle,
  SongRowWidth,
  YearHeader,
} from './shared.tsx';
import { CellHoverOutlineWidth, SongCell } from './song_cell.tsx';
import { PlannerSaveRefusalBanner } from './song_list_save_refusal_banner.tsx';
import { EditTermsButton, openTermPlanner } from './term_planner.tsx';
import { TermSelector } from './term_selector.tsx';
import { AllSongLists, getSchoolSongListSchema, useAllSongLists } from './use_all_song_lists.ts';
import { useColumnVirtualizer } from './use_column_virtualizer.ts';
import { usePlannerKeyboardNavigation } from './use_planner_keyboard_navigation.tsx';

const schoolPlannerBeginEndPaddingWeeks = 2;
const minSchoolStartDate = addDayOffset(todayDate(), -2 * 365);
const maxSchoolEndDate = addDayOffset(todayDate(), 2 * 365);

export interface CellPosition {
  row: number; // 1 based
  column: number; // 0 based
}

export const Planner = () => {
  const [syncComplete, setSyncComplete] = useState(false);
  const navigate = useNavigate();

  useEffectOnce(() => {
    void (async () => {
      await synchronizeAllSongLists();
      setSyncComplete(true);
    })();
  });

  if (!syncComplete) {
    return (
      <OuterPageContent>
        <LoadingHeader>
          <BackButton onClick={() => void goBack(navigate)} />
          <YearHeader>{`${new window.Date().getFullYear()} Liturgy Planner`}</YearHeader>
        </LoadingHeader>
        <LoadingSpinnerWrapper>
          <Spinner />
        </LoadingSpinnerWrapper>
      </OuterPageContent>
    );
  }

  return <PlannerContent />;
};

const LoadingHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 10px 10px calc(${BackButtonWidth} + ${PlannerHeaderButtonGap});
`;

const LoadingSpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

const PlannerContent = () => {
  const allSongLists = useAllSongLists();
  const navigate = useNavigate();
  const { customMusic } = useCustomMusic();
  const hymnals = use(HymnalsContext);
  const { currentOrg } = useCurrentOrg();
  const { isInternalUser, isOrgAdmin } = useUserAttributes();
  const today = useCurrentDate();
  const currentWeekDate = getWeeklyDateForOrganization(currentOrg, today);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  // a cell is committed on Enter or blur, so a deploy must not reload the page under it (#3265)
  useHoldAppReload(isEditing);
  const schema =
    currentOrg?.type === OrgType.School
      ? getSchoolSongListSchema(currentOrg, allSongLists)
      : currentOrg?.churchLiturgySchema;
  const selectedWeekRef = useRef<HTMLDivElement>(null);
  const [schoolTerms] = useLocalStorage(LocalStorageKey.SchoolTerms);

  const hymnsOfTheMonth = useMemo(
    () =>
      new Map<DateString, HymnOfTheMonthSongList>(
        (allSongLists.filter(({ type }) => type === SongListType.HymnOfTheMonth) as HymnOfTheMonthSongList[]).map(
          (songList) => [getSongListStartDate(songList), songList],
        ),
      ),
    [allSongLists],
  );
  const monthlySongLists = useMemo(
    () =>
      new Map<DateString, IntervalSongList>(
        (
          allSongLists.filter(
            (list) => list.type === SongListType.Interval && list.interval.type === IntervalType.Month,
          ) as IntervalSongList[]
        ).map((list) => [getSongListStartDate(list), list]),
      ),
    [allSongLists],
  );
  const weeklySongLists = useMemo(
    () =>
      new Map<DateString, SongList>(
        allSongLists
          .filter((songList) => isWeeklySongList(currentOrg, songList))
          .map((songList) => {
            const date = getSongListStartDate(songList);
            return [date, songList];
          }),
      ),
    [allSongLists, currentOrg],
  );

  const onBack = useCallback(() => {
    void (async () => {
      await synchronizeAllSongLists();
      if (currentOrg) {
        await refreshSingingPlans(currentOrg.id);
        await synchronizeOrgsWithServer({ force: true });
      }
      await goBack(navigate);
    })();
  }, [currentOrg, navigate]);

  const oldestPlannerDate = useMemo(
    () => getOldestPlannerDate(currentOrg, schoolTerms, allSongLists, currentWeekDate),
    [allSongLists, currentOrg, currentWeekDate, schoolTerms],
  );

  const newestPlannerDate = useMemo(() => {
    return getNewestPlannerDate(currentOrg, schoolTerms, allSongLists, currentWeekDate);
  }, [allSongLists, currentOrg, currentWeekDate, schoolTerms]);

  const { weeklyDates, columnsInYear, termHeaderInfo, columnsInMonth, datesOutsideSchoolTerm } = useMemo(() => {
    const weekCount = Math.round(daysBetween(oldestPlannerDate, newestPlannerDate) / 7) + 1;
    const columnsInYear: { [year: string]: number } = {};
    const termHeaderInfo: Array<{
      startDate: string;
      term: IntervalSongList | undefined;
      columnStart: number;
      columnCount: number;
    }> = [];
    const columnsInMonth = new Map<DateString, { columnStart: number; columnCount: number }>(); // DateString = first day of the month
    const weeklyDates: DateString[] = [];
    const date = dateFromString(oldestPlannerDate);
    const datesOutsideSchoolTerm = new Set<DateString>();
    for (let index = 0; index < weekCount; index++) {
      const weeklyDate = dateStringFromDate(date);
      weeklyDates.push(weeklyDate);
      columnsInYear[date.getFullYear()] = (columnsInYear[date.getFullYear().toString()] ?? 0) + 1;
      const firstDayOfMonth = getFirstDayOfMonth(weeklyDate);
      const currentMonthlyEntry = columnsInMonth.get(firstDayOfMonth);
      columnsInMonth.set(firstDayOfMonth, {
        columnStart: currentMonthlyEntry?.columnStart ?? index,
        columnCount: (currentMonthlyEntry?.columnCount ?? 0) + 1,
      });
      if (currentOrg?.type === OrgType.School) {
        const term = getTermForDateRange({ start: weeklyDate, end: addDayOffset(weeklyDate, 6) }, schoolTerms);
        const isDateOutsideSchoolTerms = currentOrg?.type === OrgType.School && !term;
        if (isDateOutsideSchoolTerms) {
          datesOutsideSchoolTerm.add(weeklyDate);
        }
        if (termHeaderInfo.length === 0 || term !== termHeaderInfo[termHeaderInfo.length - 1].term) {
          termHeaderInfo.push({ startDate: weeklyDate, term, columnStart: index, columnCount: 1 });
        } else {
          termHeaderInfo[termHeaderInfo.length - 1].columnCount += 1;
        }
      }
      date.setDate(date.getDate() + 7);
    }
    return {
      weeklyDates: weeklyDates as NonEmptyArray<DateString>,
      columnsInYear,
      termHeaderInfo,
      columnsInMonth,
      datesOutsideSchoolTerm,
    };
  }, [currentOrg?.type, newestPlannerDate, oldestPlannerDate, schoolTerms]);

  // Column virtualization: only render columns visible in the viewport + buffer
  const columnWidth = Math.min(window.innerWidth - 4, 300);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollLeft, setScrollLeft] = useState(() => {
    const idx = weeklyDates.indexOf(currentWeekDate);
    const target = idx !== -1 ? idx : weeklyDates.length - 1;
    return Math.max(0, target * columnWidth + columnWidth / 2 - window.innerWidth / 2);
  });
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);

  const { firstVisibleIndex, lastVisibleIndex } = useColumnVirtualizer({
    scrollLeft,
    containerWidth,
    columnWidth,
    totalColumnCount: weeklyDates.length,
  });

  const rafRef = useRef<number | undefined>(undefined);
  const onScroll = useCallback(() => {
    if (rafRef.current !== undefined) {
      return;
    }
    rafRef.current = requestAnimationFrame(() => {
      const el = scrollContainerRef.current;
      if (el) {
        setScrollLeft(el.scrollLeft);
        setContainerWidth(el.clientWidth);
      }
      rafRef.current = undefined;
    });
  }, []);

  const [selectedColumnIndex, setSelectedColumnIndex] = useState<number | undefined>();
  const selectedColumnDate =
    selectedColumnIndex !== undefined && selectedColumnIndex < weeklyDates.length
      ? weeklyDates[selectedColumnIndex]
      : undefined;

  useEffectOnce(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const idx = weeklyDates.indexOf(currentWeekDate);
      const target = idx !== -1 ? idx : weeklyDates.length - 1;
      container.scrollLeft = Math.max(0, target * columnWidth + columnWidth / 2 - container.clientWidth / 2);
    }
  });

  function getDayElementId(date: DateString): string {
    return `planner-day-${date}`;
  }

  const isColumnVisible = useCallback(
    (columnIndex: number) => columnIndex >= firstVisibleIndex && columnIndex <= lastVisibleIndex,
    [firstVisibleIndex, lastVisibleIndex],
  );

  const isColumnRangeVisible = useCallback(
    (columnStart: number, columnCount: number) => {
      const columnEnd = columnStart + columnCount - 1;
      return columnEnd >= firstVisibleIndex && columnStart <= lastVisibleIndex;
    },
    [firstVisibleIndex, lastVisibleIndex],
  );

  const rowCount = usePlannerRowCount({ org: currentOrg, allSongLists, dates: weeklyDates });

  const getColumnRangeForColumn = useCallback(
    (rowIndex: number, column: number): { columnStart: number; columnCount: number } => {
      const rowSchema = schema?.rows.find((row) => row.row === rowIndex);
      let result: { columnStart: number; columnCount: number } | undefined;
      if (rowSchema?.intervalType === IntervalType.Month) {
        result = columnsInMonth
          .values()
          .find(({ columnStart, columnCount }) => column >= columnStart && column < columnStart + columnCount);
      }
      if (!result && rowSchema?.intervalType === IntervalType.Custom) {
        result = termHeaderInfo.find(
          ({ columnStart, columnCount }) => column >= columnStart && column < columnStart + columnCount,
        ) ?? { columnStart: column, columnCount: 1 };
      }
      return result ?? { columnStart: column, columnCount: 1 };
    },
    [columnsInMonth, schema?.rows, termHeaderInfo],
  );

  const nextColumn = useCallback(
    (row: number, column: number) => {
      const { columnStart, columnCount } = getColumnRangeForColumn(row, column);
      return (columnStart + columnCount) % weeklyDates.length;
    },
    [getColumnRangeForColumn, weeklyDates.length],
  );
  const prevColumn = useCallback(
    (row: number, column: number) => {
      const { columnStart } = getColumnRangeForColumn(row, column);
      return (columnStart - 1 + weeklyDates.length) % weeklyDates.length;
    },
    [getColumnRangeForColumn, weeklyDates.length],
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { moveDown, onKeyDown, selectedCell, setSelectedCell } = usePlannerKeyboardNavigation({
    defaultColumn: weeklyDates.indexOf(currentWeekDate) !== -1 ? weeklyDates.indexOf(currentWeekDate) : 0,
    rowCount,
    nextColumn,
    prevColumn,
    ignoreKeystrokes: isDialogOpen,
    columnCount: weeklyDates.length,
  });
  const selectedCellDate = weeklyDates[selectedCell.column];

  // Scroll to keep the selected cell visible when navigating via keyboard
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }
    const cellLeft = selectedCell.column * columnWidth;
    const cellRight = cellLeft + columnWidth;
    const viewLeft = container.scrollLeft;
    const viewRight = container.scrollLeft + container.clientWidth;
    if (cellLeft < viewLeft) {
      container.scrollLeft = cellLeft;
    } else if (cellRight > viewRight) {
      container.scrollLeft = cellRight - container.clientWidth;
    }
  }, [selectedCell.column, columnWidth]);

  useEffect(() => {
    if (selectedColumnIndex !== undefined) {
      setSelectedCell({ row: 1, column: selectedColumnIndex });
    }
  }, [selectedColumnIndex, setSelectedCell]);

  const setSelectedCellWrapper = useCallback(
    (value: CellPosition) => {
      setSelectedColumnIndex(undefined);
      setSelectedCell(value);
    },
    [setSelectedCell],
  );

  const getSongCell = useCallback(
    ({
      rowIndex,
      songList,
      date,
      columnStart,
      columnCount,
      songListType,
    }: {
      rowIndex: number;
      songList: SongList;
      date: string;
      columnStart: number;
      columnCount: number;
      songListType: SongListType;
    }) => {
      const row = schema?.rows.find(({ row: current }) => current === rowIndex);
      const label = getPlatform() === Platform.Mobile ? row?.label : undefined;
      return (
        <SongCell
          allSongLists={allSongLists}
          key={`${songList?.id ?? date}-${rowIndex}`}
          customMusic={customMusic}
          date={date}
          hymnals={hymnals}
          label={label}
          songList={songList}
          songListType={songListType}
          songListEntryType={row?.type ?? SongListEntryType.Song}
          selectedCell={selectedColumnIndex !== undefined ? undefined : selectedCell}
          setSelectedCell={setSelectedCellWrapper}
          setIsDialogOpen={setIsDialogOpen}
          setIsEditing={setIsEditing}
          moveDown={moveDown}
          isInternalUser={isInternalUser}
          isOrgAdmin={isOrgAdmin}
          row={rowIndex}
          columnStart={columnStart}
          columnCount={columnCount}
        />
      );
    },
    [
      schema?.rows,
      allSongLists,
      customMusic,
      hymnals,
      selectedColumnIndex,
      selectedCell,
      setSelectedCellWrapper,
      moveDown,
      isInternalUser,
      isOrgAdmin,
    ],
  );

  function getHymnOfTheMonth(
    songList: HymnOfTheMonthSongList | undefined,
    date: DateString,
    columnStart: number,
    columnCount: number,
  ) {
    return (
      <SongCell
        allSongLists={allSongLists}
        key={`${songList?.id ?? date}`}
        customMusic={customMusic}
        date={date}
        hymnals={hymnals}
        row={1}
        label={`${getMonthName(date)} Hymn of the Month`}
        songList={
          songList ?? {
            date,
            type: SongListType.HymnOfTheMonth,
            interval: { type: IntervalType.Month, start: date, end: getLastDayOfMonth(date) },
            songs: [],
          }
        }
        songListType={SongListType.HymnOfTheMonth}
        songListEntryType={SongListEntryType.Song}
        isInternalUser={isInternalUser}
        isOrgAdmin={isOrgAdmin}
        columnStart={columnStart}
        columnCount={columnCount}
      />
    );
  }

  const getCellValues = useCallback((row: string): string[] => {
    return row.split('\t');
  }, []);

  const getCopiedRows = useCallback(
    (text: string): string[] => {
      const rows = text.split('\n');
      if (selectedColumnIndex === undefined || rows.length !== 1) {
        return rows;
      }
      // special case - (single row copy + column selection paste) => convert columns to rows for paste
      return getCellValues(rows[0]);
    },
    [getCellValues, selectedColumnIndex],
  );

  const onPaste = useCallback(
    (event: ClipboardEvent) => {
      console.log('onPaste');
      if (isEditing) {
        return; // Allow currently open editor to handle paste
      }
      const text = event.clipboardData?.getData('text/plain').replace(/\r/g, ''); // strip Microsoft cruft
      if (text && selectedCell) {
        let updatedSongLists: SongList[] = [];
        const rows = getCopiedRows(text);
        for (const [rowIndex, rowValue] of rows.entries()) {
          const cells = getCellValues(rowValue);
          for (const [cellIndex, cellValue] of cells.entries()) {
            const date = addDayOffset(selectedCellDate, 7 * cellIndex);
            // TODO(hewitt): handle copy/paste for hymn of the month cells (rectangular selection)
            //               currently filtering on the weekly song list type
            //               see both calls to getSongListStartDate below
            const matchingSongList = (list: SongList) =>
              getSongListStartDate(list) === date && list.type === getWeeklySongListType(currentOrg);
            let songList: SongList =
              updatedSongLists.find(matchingSongList) ??
              allSongLists.find(matchingSongList) ??
              getEmptyWeeklySongList(currentOrg, date);
            const row = selectedCell.row + rowIndex;
            let entry: SongListEntry | undefined;
            const schemaRow = schema?.rows?.find(({ row: currentRow }) => currentRow === row);
            const entryType = schemaRow?.type ?? SongListEntryType.Song;
            const slug =
              entryType === SongListEntryType.Song &&
              resolveSearchStringToSongSlug(
                hymnals,
                currentOrg && customMusic[currentOrg?.id],
                cellValue,
                isInternalUser,
              );
            if (slug) {
              entry = { row, slug };
            } else if (cellValue) {
              entry = { row, text: cellValue };
            }
            if (entry) {
              songList = { ...songList, songs: [...songList.songs.filter((song) => song.row !== row), entry] };
              updatedSongLists = [...updatedSongLists.filter((list) => getSongListStartDate(list) !== date), songList];
            }
          }
        }
        void allSongLists.updateEach(updatedSongLists);
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [
      allSongLists,
      currentOrg,
      customMusic,
      getCellValues,
      getCopiedRows,
      hymnals,
      isEditing,
      isInternalUser,
      schema?.rows,
      selectedCell,
      selectedCellDate,
    ],
  );

  const onCopy = useCallback(() => {
    const date = selectedColumnDate ?? selectedCellDate;
    if (!date || !currentOrg) {
      return;
    }
    let text: string | undefined;
    const songLists = allSongLists.filter(
      (list) => list.type !== SongListType.Interval || dateRangeOverlapsDate(list.interval, date),
    );
    const songEntries = getSongEntriesForDate(currentOrg, date, songLists, Object.values(SongListEntryType));
    if (selectedColumnDate) {
      text = '';
      let firstEntry = true;
      const maxRow = Math.max(...(songEntries.map(({ row }) => row) ?? []), 0);
      for (let currentRow = 1; currentRow <= maxRow; currentRow++) {
        const entry = songEntries.find(({ row }) => row === currentRow);
        if (!firstEntry) {
          text += '\n';
        }
        text += getEntryText(entry) ?? '';
        firstEntry = false;
      }
    } else if (selectedCell) {
      const entry = songEntries?.find(({ row }) => row === selectedCell.row);
      text = getEntryText(entry);
    }
    if (text) {
      void navigator.clipboard.writeText(text);
    }

    function getEntryText(entry: SongListEntry | undefined): string | undefined {
      return isBound(entry) ? getDisplayNameForSongListEntry({ entry: entry, hymnals, customMusic }) : entry?.text;
    }
  }, [allSongLists, currentOrg, customMusic, hymnals, selectedCell, selectedCellDate, selectedColumnDate]);

  useEffect(() => {
    const controller = new AbortController();
    window.addEventListener('paste', onPaste, { signal: controller.signal });
    window.addEventListener('copy', onCopy, { signal: controller.signal });
    return () => controller.abort();
  }, [onPaste, onCopy]);

  const onLocalKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Backspace' || event.key === 'Delete') {
        // only the column's own weekly list: matching on start date alone cleared that month's hymn of the
        // month instead whenever the Sunday fell on the 1st (issue #3609)
        if (selectedColumnIndex !== undefined) {
          const songList = allSongLists.find((list) => isWeeklySongListForDate(currentOrg, list, selectedColumnDate));
          if (songList) {
            void allSongLists.update({ ...songList, songs: [] });
          }
        }
      }
    },
    [allSongLists, currentOrg, selectedColumnDate, selectedColumnIndex],
  );

  useEffect(() => {
    if (selectedColumnIndex !== undefined) {
      selectedWeekRef.current?.focus();
    }
  }, [selectedColumnIndex, selectedWeekRef]);

  const headerContent = useCallback(
    (title: ReactElement) => (
      <HeaderContent>
        <BackButton key="back" onClick={onBack} />
        <BackButtonPlaceholder />
        {title}
        {allSongLists.containsUpdates && <SyncAnimation $size="30px" />}
      </HeaderContent>
    ),
    [allSongLists.containsUpdates, onBack],
  );

  const dateHeaders = [...weeklyDates.entries()]
    .filter(([index]) => isColumnVisible(index))
    .map(([index, date]) => (
      <DateHeader
        key={date}
        id={getDayElementId(date)}
        highlight={date === currentWeekDate}
        columnIndex={index}
        setSelectedColumnIndex={setSelectedColumnIndex}
        isOutsideSchoolTerms={datesOutsideSchoolTerm.has(date)}
      >
        {titleFromDate(currentOrg, date, schoolTerms)}
        {currentOrg && <DownloadSongSheetsButton org={currentOrg} date={date} />}
      </DateHeader>
    ));

  const hasHymnOfTheMonth = currentOrg?.type !== OrgType.School;

  const gridCells = useMemo(
    () =>
      [...Array(rowCount).keys()]
        .map((index) => index + 1)
        .map((rowIndex) => {
          const rowSchema = schema?.rows.find(({ row }) => row === rowIndex);
          const intervalType = rowSchema?.intervalType;
          const songListType = intervalType ? SongListType.Interval : getWeeklySongListType(currentOrg);

          if (songListType === SongListType.Interval) {
            // termly songs
            if (intervalType === IntervalType.Custom) {
              return termHeaderInfo
                .filter(({ term, columnStart, columnCount }) => term && isColumnRangeVisible(columnStart, columnCount))
                .map(({ startDate, term, columnStart, columnCount }) =>
                  getSongCell({
                    rowIndex,
                    songList: term as IntervalSongList,
                    date: startDate,
                    columnStart,
                    columnCount,
                    songListType,
                  }),
                );
            }
            if (intervalType === IntervalType.Month) {
              return [...columnsInMonth]
                .map(([firstDayOfMonth, { columnStart, columnCount }]) => ({
                  firstDayOfMonth,
                  columnStart,
                  columnCount,
                  interval: {
                    type: IntervalType.Month,
                    start: firstDayOfMonth,
                    end: getLastDayOfMonth(firstDayOfMonth),
                  },
                }))
                .filter(
                  ({ interval, columnStart, columnCount }) =>
                    getTermForDateRange(interval, schoolTerms) && isColumnRangeVisible(columnStart, columnCount),
                )
                .map(({ firstDayOfMonth, columnStart, columnCount, interval }) => {
                  return getSongCell({
                    rowIndex,
                    songList: monthlySongLists.get(firstDayOfMonth) ?? {
                      type: SongListType.Interval,
                      interval,
                      songs: [],
                    },
                    date: firstDayOfMonth,
                    columnStart,
                    columnCount,
                    songListType,
                  });
                });
            }
          }

          return [...weeklyDates.entries()]
            .filter(
              ([columnIndex, weeklyDate]) => !datesOutsideSchoolTerm.has(weeklyDate) && isColumnVisible(columnIndex),
            )
            .map(([columnIndex, weeklyDate]) => {
              const songList = weeklySongLists.get(weeklyDate) ?? getEmptyWeeklySongList(currentOrg, weeklyDate);
              return getSongCell({
                rowIndex,
                songList,
                date: weeklyDate,
                columnStart: columnIndex,
                columnCount: 1,
                songListType,
              });
            });
        }),
    [
      currentOrg,
      columnsInMonth,
      datesOutsideSchoolTerm,
      getSongCell,
      isColumnRangeVisible,
      isColumnVisible,
      monthlySongLists,
      rowCount,
      schema?.rows,
      schoolTerms,
      termHeaderInfo,
      weeklyDates,
      weeklySongLists,
    ],
  );

  const disabledWeeks = useMemo(() => {
    if (currentOrg?.type !== OrgType.School) {
      return null;
    }
    return [...weeklyDates.entries()]
      .filter(([index, weeklyDate]) => datesOutsideSchoolTerm.has(weeklyDate) && isColumnVisible(index))
      .map(([columnIndex, weeklyDate]) => (
        <DisabledColumn
          key={weeklyDate}
          date={weeklyDate}
          columnIndex={columnIndex}
          rowCount={rowCount}
          allSongLists={allSongLists}
        />
      ));
  }, [allSongLists, currentOrg?.type, datesOutsideSchoolTerm, isColumnVisible, rowCount, weeklyDates]);

  const onSelectTerm = useCallback(
    (term: IntervalSongList) => {
      const container = scrollContainerRef.current;
      if (!container) {
        return;
      }
      const termDate = getWeeklyDateForOrganization(currentOrg, term.interval.start);
      const columnIndex = weeklyDates.indexOf(termDate);
      if (columnIndex !== -1) {
        container.scrollTo({
          left: Math.max(0, columnIndex * columnWidth + columnWidth / 2 - container.clientWidth / 2),
          behavior: 'smooth',
        });
      }
    },
    [columnWidth, currentOrg, weeklyDates],
  );

  const headers: Array<{ key: string; columnCount: number; content: ReactElement }> = useMemo(() => {
    return currentOrg?.type === OrgType.School
      ? termHeaderInfo.map(({ startDate, term, columnCount }) => ({
          key: startDate,
          columnCount,
          content:
            term || termHeaderInfo.length === 1 ? (
              headerContent(<TermSelector term={term} onSelectTerm={onSelectTerm} />)
            ) : (
              <div />
            ),
        }))
      : Object.entries(columnsInYear).map(([year, columnCount]) => ({
          key: year,
          columnCount,
          content: headerContent(<YearHeader key="header">{`${year} Liturgy Planner`}</YearHeader>),
        }));
  }, [currentOrg?.type, columnsInYear, headerContent, onSelectTerm, termHeaderInfo]);

  const onOpenDashboard = useCallback(async () => {
    await navigate(getUrlForPage(Pages.Dashboard));
  }, [navigate]);

  return (
    <>
      <OuterPageContent key="outer">
        <InnerPageContent key="inner" ref={scrollContainerRef} onScroll={onScroll}>
          <PlannerSchemaWrapper key="planner-schema-wrapper">
            {getPlatform() !== Platform.Mobile && (
              <PlannerSchemaColumn key="planner-schema-column" allSongLists={allSongLists} songRowCount={rowCount} />
            )}
            <PlannerDataWrapper>
              <TitleStacker key="year-term-stacker">
                {headers.map(({ key, columnCount, content }) => (
                  <TitleWrapper key={key} $columnCount={columnCount}>
                    <TitleHorizontalWrapper>
                      <TitleInnerWrapper>{content}</TitleInnerWrapper>
                    </TitleHorizontalWrapper>
                  </TitleWrapper>
                ))}
                <PlannerButtonBar>
                  {getPlatform() !== Platform.Mobile && <EditTermsButton styled={true} />}
                  <UploadSongButton />
                  {(isInternalUser || isOrgAdmin) && <DashboardButton onClick={onOpenDashboard} />}
                </PlannerButtonBar>
              </TitleStacker>
              <DateHeaders key={'date-headers'} $totalColumns={weeklyDates.length}>
                {dateHeaders}
              </DateHeaders>
              {currentOrg?.type !== OrgType.School && (
                <RowWrapper
                  key={'hymn-of-the-month-row'}
                  $height={SongRowHeightDouble}
                  $totalColumns={weeklyDates.length}
                >
                  {[...columnsInMonth]
                    .filter(([_firstDay, { columnStart, columnCount }]) =>
                      isColumnRangeVisible(columnStart, columnCount),
                    )
                    .map(([firstDayOfMonth, { columnStart, columnCount }]) =>
                      getHymnOfTheMonth(
                        hymnsOfTheMonth.get(firstDayOfMonth),
                        firstDayOfMonth,
                        columnStart,
                        columnCount,
                      ),
                    )}
                </RowWrapper>
              )}
              <PlannerGrid
                $rows={rowCount}
                $cols={weeklyDates.length}
                $hasHymnOfTheMonth={hasHymnOfTheMonth}
                $rowHeight={getPlatform() === Platform.Mobile ? SongRowHeightDouble : SongRowHeightSingle}
                onKeyDown={onKeyDown}
              >
                {disabledWeeks}
                {gridCells}
                {selectedColumnIndex !== undefined && selectedColumnIndex > 0 && (
                  <SelectedColumnOutline
                    key={`select-column`}
                    tabIndex={0}
                    onKeyDown={onLocalKeyDown}
                    ref={selectedWeekRef}
                    style={{ gridRowStart: 1, gridRowEnd: rowCount + 1, gridColumn: selectedColumnIndex + 1 }}
                  />
                )}
              </PlannerGrid>
            </PlannerDataWrapper>
          </PlannerSchemaWrapper>
        </InnerPageContent>
        {/* below the scroll container, not above it: the button bar is fixed to the top of the window, and
            the sticky headers stick to the scroll container, which only gets shorter */}
        <PlannerSaveRefusalBanner />
      </OuterPageContent>
    </>
  );
};

const PlannerButtonBar = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: ${PlannerYearHeight};
  padding: 0 15px;
  background-color: var(--color-background);
  display: flex;
  align-items: center;
  gap: 5px;
`;

function DashboardButton({ onClick }: { onClick: () => void }) {
  return (
    <div>
      <DashboardButtonText onClick={onClick}>Dashboard</DashboardButtonText>
      <DashboardIconWrapper onClick={onClick} />
    </div>
  );
}

const DashboardButtonText = styled.button`
  color: var(--color-text);
  font-family: Jost-SemiBold, Arial, sans-serif;
  border: 3px solid var(--color-text);
  border-radius: ${HeaderButtonBorderRadius};
  padding: ${HeaderButtonPaddingDesktop};
  cursor: pointer;
  &:active {
    color: var(--color-background);
    background-color: var(--color-text);
  }
  @media (max-width: ${PlannerButtonIconThreshold}) {
    display: none;
  }
`;

const DashboardIconWrapper = styled(DashboardIcon)`
  width: 30px;
  height: 30px;
  fill: var(--color-text);
  cursor: pointer;
  @media (min-width: ${PlannerButtonIconThreshold}) {
    display: none;
  }
`;

const RowWrapper = styled.div<{ $height: string; $totalColumns: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$totalColumns}, ${SongRowWidth});
  grid-template-rows: ${(props) => props.$height};
  height: ${(props) => props.$height};
  text-align: left;
`;

const PlannerGrid = styled.div<{ $rows: number; $cols: number; $hasHymnOfTheMonth: boolean; $rowHeight: string }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$cols}, ${SongRowWidth});
  grid-template-rows: repeat(${(props) => props.$rows}, ${(props) => props.$rowHeight});
  text-align: left;
`;

function titleFromDate(church: Org | undefined, date: DateString, schoolTerms: IntervalSongList[]) {
  const shortDate = `${getDateAsShortString(date)}`;

  if (church?.type !== OrgType.School) {
    const day = Date.fromString(date);
    // FIXME(lukesneeringer): Put in a smarter way to think about "The" prefixes.
    const liturgicalLabel = day.label('#F', '#-O #-D #P #S').replace(/^The (?=[BNRS])/, '');
    return day?.format(`%b %-d: ${liturgicalLabel}`);
  }

  const week: DateRange = { start: date, end: addDayOffset(date, 6) };
  const term = getTermForDateRange(week, schoolTerms);
  if (!term) {
    return shortDate;
  }
  return `Week ${getTermWeekNumber(term, week)} - ${shortDate}`;
}

const InnerPageContent = styled.div`
  flex: auto;
  overflow: scroll;
  overscroll-behavior-x: none; /* avoid swipe to go back */
  overscroll-behavior-y: none;
  max-width: 100vw;
  /* check out scroll-snap-points */
  /*scroll-snap-points-y: repeat(100vh);*/
  /*scroll-snap-type: y mandatory;*/
  /*scroll-snap-align: start; - on child */
`;

const TitleStacker = styled.div`
  display: flex;
  width: fit-content;
  text-align: left;
  z-index: 6;
  position: sticky;
  top: 0;
`;

const TitleWrapper = styled.div<{ $columnCount: number }>`
  display: flex;
  flex-direction: column;
  width: calc(${(props) => props.$columnCount} * ${SongRowWidth});
`;

const TitleHorizontalWrapper = styled.div`
  background-color: var(--color-background);
  height: ${PlannerYearHeight};
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  gap: ${PlannerHeaderButtonGap};
  background-color: var(--color-background);
`;

const TitleInnerWrapper = styled.div`
  position: sticky;
  left: 0;
  width: fit-content;
  height: ${PlannerYearHeight};
  display: flex;
  align-items: center;
`;

const DateHeaders = styled.div<{ $totalColumns: number }>`
  z-index: 4;
  position: sticky;
  top: ${DateRowTop};
  width: calc(${(props) => props.$totalColumns} * ${SongRowWidth});
  height: ${SongRowHeightSingle};
`;

function DateHeader({
  highlight,
  id,
  children,
  setSelectedColumnIndex,
  isOutsideSchoolTerms,
  columnIndex,
}: {
  highlight: boolean;
  id: string;
  setSelectedColumnIndex: (index: number | undefined) => void;
  isOutsideSchoolTerms: boolean;
  children: React.ReactNode;
  columnIndex: number;
}) {
  const onClick = useCallback(() => {
    if (!isOutsideSchoolTerms) {
      setSelectedColumnIndex(columnIndex);
    }
  }, [columnIndex, isOutsideSchoolTerms, setSelectedColumnIndex]);
  return (
    <DateHeaderWrapper
      id={id}
      $highlight={highlight}
      $isOutsideSchoolTerms={isOutsideSchoolTerms}
      $columnIndex={columnIndex}
      onClick={onClick}
    >
      {children}
    </DateHeaderWrapper>
  );
}

const DateHeaderWrapper = styled.div<{ $highlight: boolean; $isOutsideSchoolTerms: boolean; $columnIndex: number }>`
  position: absolute;
  left: calc(${(props) => props.$columnIndex} * ${SongRowWidth});
  width: ${SongRowWidth};
  height: ${SongRowHeightSingle};
  font-family: Jost-SemiBold, Arial, sans-serif;
  outline: 1px solid var(--color-text); /* outline instead of border takes up 0 space */
  padding: 0 10px;
  color: ${(props) => (props.$highlight ? 'var(--color-date-highlight-text)' : 'var(--color-text)')};
  background-color: var(--color-planner-row-header);
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${(props) => (props.$isOutsideSchoolTerms ? '' : 'cursor: pointer;')}
`;

const SelectedColumnOutline = styled.div`
  z-index: 1;
  width: ${SongRowWidth};
  height: 100%;
  outline: ${CellHoverOutlineWidth} solid var(--color-text);
  pointer-events: none;
`;

function DisabledColumn({
  date,
  rowCount,
  columnIndex,
  allSongLists,
}: {
  date: DateString;
  rowCount: number;
  columnIndex: number;
  allSongLists: AllSongLists;
}) {
  const onMouseDownDisabled = useCallback(() => {
    void (async () => {
      if (await confirm({ confirmation: <span>Would you like to create a term?</span> })) {
        const schoolTerms = localStorageGet(LocalStorageKey.SchoolTerms);
        const overlappingTerm = getTermForDateRange({ start: date, end: date }, schoolTerms);
        const start = overlappingTerm ? addDayOffset(overlappingTerm.interval.start, 1) : date;
        const end = addDayOffset(start, 6);
        await allSongLists.addCustomInterval({ start, end });
        await openTermPlanner({ _unused: 'hi' });
      }
    })();
  }, [allSongLists, date]);

  return (
    <DisabledColumnWrapper
      key={`disabled-column-${date}`}
      style={{ gridRowStart: 1, gridRowEnd: rowCount + 1, gridColumn: columnIndex + 1 }}
      onClick={onMouseDownDisabled}
    />
  );
}

const DisabledColumnWrapper = styled.div`
  background-color: var(--color-background-button-disabled);
`;

const PlannerSchemaWrapper = styled.div`
  display: flex;
  width: max-content;
`;

const PlannerDataWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

function getEmptyWeeklySongList(church: Org | undefined, date: DateString): EventSongList | IntervalSongList {
  const type = church?.type ?? OrgType.Church;
  switch (type) {
    case OrgType.Church:
    case OrgType.SmallGroup:
      return { type: SongListType.WorshipService, date: date, songs: [] };
    case OrgType.School:
      return {
        type: SongListType.Interval,
        interval: { type: IntervalType.Week, start: date, end: addDayOffset(date, 6) },
        songs: [],
      };
    default:
      ensureUnreachable(type);
  }
}

function usePlannerRowCount({
  org,
  allSongLists,
  dates,
}: {
  org: Org | undefined;
  allSongLists: AllSongLists;
  dates: NonEmptyArray<DateString>;
}): number {
  const maxSongListRow = useMemo(
    () =>
      Math.max(
        ...allSongLists
          .filter(
            (list) => getSongListStartDate(list) >= dates[0] && getSongListStartDate(list) <= dates[dates.length - 1],
          )
          .flatMap(({ songs }) => songs)
          .map(({ row }) => row),
      ),
    [allSongLists, dates],
  );
  if (org?.type === OrgType.School) {
    const schema = getSchoolSongListSchema(org, allSongLists);
    return schema?.rows.length;
  } else {
    const maxPlannerRow = Math.max(...(org?.churchLiturgySchema?.rows.map(({ row }) => row) ?? []), 0);
    return Math.max(MinSongListRowCount, maxSongListRow + 1, maxPlannerRow + 1);
  }
}

function getOldestPlannerDate(
  church: ClientOrg | undefined,
  schoolTerms: IntervalSongList[],
  allSongLists: AllSongLists,
  currentWeekDate: string,
) {
  let startDate: DateString;
  if (church?.type === OrgType.School) {
    const firstTerm = schoolTerms?.length ? schoolTerms[0] : undefined;
    const firstTermStart = firstTerm?.interval.start ?? todayDate();
    startDate = maxDate([minSchoolStartDate, addDayOffset(firstTermStart, -schoolPlannerBeginEndPaddingWeeks * 7)]);
  } else {
    const earliestSongListDate = minDate([...allSongLists.map((list) => getSongListStartDate(list)), currentWeekDate]);
    startDate = earliestSongListDate;
  }
  return getWeeklyDateForOrganization(church, startDate);
}

function getNewestPlannerDate(
  church: ClientOrg | undefined,
  schoolTerms: IntervalSongList[],
  allSongLists: AllSongLists,
  currentWeekDate: string,
) {
  let endDate: DateString;
  if (church?.type === OrgType.School) {
    const lastTerm = schoolTerms?.length ? schoolTerms[schoolTerms.length - 1] : undefined;
    const lastTermEnd = lastTerm?.interval.end ?? todayDate();
    endDate = minDate([maxSchoolEndDate, addDayOffset(lastTermEnd, schoolPlannerBeginEndPaddingWeeks * 7)]);
  } else {
    const newestDate = maxDate([...allSongLists.map((list) => getSongListEndDate(list)), currentWeekDate]);
    endDate = addDayOffset(newestDate, songFutureSizeInWeeks * 7);
  }
  return getWeeklyDateForOrganization(church, endDate);
}
